import EmptyData from "features/analytics/Components/EmptyData";
import { TickStyle } from "features/analytics/Style";
import { upsertCustomerFeedback } from "features/journey-map/reducer/CJMActions";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Spinner } from "shared/components/spinners/Spinner";
import { FlexAlignJustifyCenter, IconDispatcher, Label } from "shared/ui-basics";
import GenericSwitch from "shared/ui-basics/buttons";
import styled from "styled-components";
import { getEmotionLevelData, hexToHSL } from "utils/helpers";
import * as Styles from "../Styles";

type Props = {
    feedback: any;
    showStopRetrieveData: boolean;
};

export default function ReportSummeryFeedback({ feedback, showStopRetrieveData }: Props): JSX.Element {
    const { t } = useTranslation();
    const { cjmIdStr } = useLocation()?.search.getMapsParams();
    const connector = useSelector((state) => state.settings.connector)?.getById(feedback?.connectorId) || {};
    const { enableConnection = true } = connector;

    const { smileyColor } = getEmotionLevelData({ level: feedback?.customerFeedbackDatas?.[0]?.sentimentIndex });


    const loading = feedback?.customerFeedbackDatas?.length === 0 && (!feedback?.errorMsg || feedback?.errorMsg.isEmptyString());
    const [toggle, setToggle] = useState(feedback?.stopRetrieveData);

    const handleChange = () => {
        setToggle(!toggle);
        upsertCustomerFeedback({ customerFeedback: { ...feedback, stopRetrieveData: !toggle, errorMsg: "" }, cjmId: cjmIdStr });
    };
    const data = feedback?.customerFeedbackDatas

        ?.sort((a, b) => new Date(a.interactionDate) - new Date(b.interactionDate))
        ?.slice(-12)
        ?.map(({ interactionDate, sentimentIndex, averageRating }) => {
            const dateObject = new Date(interactionDate); // Create a single Date object
            const date = interactionDate.split("T")[0]; // Extract the date part (YYYY-MM-DD)
            const day = dateObject.getDate(); // Extract the day
            const month = dateObject.toLocaleString('en-US', { month: 'long' }); // Get the month name

            return {
                date,
                month: `${day} ${month}`, // Format as "day month"
                sentiment: sentimentIndex,
                rating: averageRating,
            };
        });
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const value = payload[0]?.payload;
            return <TooltipContainer className="custom-tooltip p-2" style={{ background: "white" }}>
                <div className="d-flex flex-column">
                    <div>{`Date: ${value?.date}`}</div>
                    <div>{`Sentiment: ${value?.sentiment}`}</div>
                    <div>{`Avg Rating: ${value?.rating}`}</div>
                </div>


            </TooltipContainer>
        }

        return null;
    };
    return <>
        {loading ? (
            <div className="pt-1 position-relative">
                <Styles.LabelCenter className="pt-3 mt-2">{t("START_RETRIEVING_DEC")}</Styles.LabelCenter>
                <Styles.LabelCenter>{t("START_RETRIEVING_DEC1")}</Styles.LabelCenter>
                <div className="position-relative mt-5 ">
                    <Spinner isFixed={false} />
                </div>
            </div>
        ) : (
            <div className="py-4">
                {feedback?.errorMsg && feedback?.errorMsg !== "" && (
                    <WrapperInfo type="error">
                        <div>{IconDispatcher("triangle-exclamation", "", { style: "solid", color: "#D50000" })}</div>
                        <Label size={12} color="#D50000">
                            {t("ERROR")} : {feedback?.errorMsg}
                        </Label>
                    </WrapperInfo>
                )}
                {!enableConnection && (
                    <WrapperInfo type="info">
                        <div>{IconDispatcher("circle-info", "", { style: "solid", color: "#0078E7" })}</div>
                        <Label size={12} color="#0078E7">
                            {t("INFO_CONNECTOR_DISABLE")}
                        </Label>
                    </WrapperInfo>
                )}
                {showStopRetrieveData && (
                    <div className="d-flex pb-5">
                        <Label className="pe-4">{t("STOP_RETRIEVE_DATA")}</Label>
                        <GenericSwitch isChecked={toggle} handleToggle={handleChange} />
                    </div>
                )}
                <div style={{ width: '100%', height: 400 }}>
                    {feedback?.customerFeedbackDatas?.length > 0 ? (
                        <ResponsiveContainer>
                            <AreaChart
                                data={data}
                                margin={{
                                    top: 25,
                                    right: 30,
                                    // bottom: 20
                                }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis allowDataOverflow dataKey="month" axisLine={{ stroke: "#c4c4c4", strokeWidth: 2 }} tick={{ ...TickStyle }} />
                                <YAxis domain={[1, 5]} tickCount={5} axisLine={false} tick={{ ...TickStyle }} />
                                <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
                                <Area type="linear" dataKey="sentiment" stroke={smileyColor} strokeWidth="2" fill={hexToHSL(smileyColor, 94, 45)} fillOpacity={0.3} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <FlexAlignJustifyCenter className="w-100 h-100">
                            <EmptyData text={t("NO_DATA_AVAILABLE")} icon="CHART-AREA" />
                        </FlexAlignJustifyCenter>
                    )}
                </div >
            </div>)}
    </>
}
export const WrapperInfo = styled.div`
	margin-bottom: 1rem;
	display: flex;
	align-items: flex-start;
	${(props) =>
        props.type === "info" &&
        `
        border: 1px solid #CBE6FF;
        background: #EAF5FF;
    `}
	${(props) =>
        props.type === "error" &&
        `
        border: 1px solid #FFD2D2;
        background: #FFEDED;
    `}

    padding:4px;
	border-radius: 4px;
	gap: 8px;
`;
const TooltipContainer = styled.div`
	color: #605f60;
	font-family: "Inter";
	font-size: 14px;
	display: flex;
	justify-content: center;
	border: 1px solid #ccc;
	min-width: 120px;
`;